import { Autocomplete, Box, InputLabel, Stack, TextField, Typography } from '@mui/material';
import { Icon, Card, InlineStack } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import i18n from 'src/locales/i18n';
import { isObject } from 'lodash';
import { inputStyles, errorInputBgStyles } from 'src/utils/style';
import { useGetCommonCodeType } from 'src/api/common_code';
import { chooseLan } from 'src/utils/language_code';
import Flag from 'react-world-flags';

export function SelectCurrency({
  value,
  setValue,
  displayName,
  placeholder,
  customerId,
  supplierId,
  disabled,
  onlyUniqueItems = false,
  countryCode,
  hideSearchIcon = false,
  dropdownFitContent = false,
  valueAsCode = false,
  error = false,
  ...others
}) {
  const [text, setText] = useState(value ? (isObject(value) ? value?.value : value) : '');
  const ref = useRef();

  const { searchResults } = useGetCommonCodeType({
    codeType: 'currency_code',
    isActive: true,
  });

  const options = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];

    const currencyData = searchResults.find((code) => code.codeType === 'currency_code');
    if (!currencyData || !currencyData.details) return [];

    return currencyData.details
      .filter((detail) => detail.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((code) => {
        return {
          label: chooseLan(code.codeValueNameLocal, code.codeValueNameEnglish),
          value: code.codeValue,
          symbol: code.codeReference1 || code.codeValue,
          country: code.codeReference2 || code.codeValueNameEnglish,
          countryCode: code.codeReference3 || code.codeValue.substring(0, 2).toUpperCase(),
        };
      });
  }, [searchResults]);
  const displayOf = (option) => (valueAsCode ? `${option.value}(${option.symbol})` : option.label);

  const handleClick = useCallback(
    (option) => {
      ref.current.ref.current.blur();
      setValue(option.value);
      setText(displayOf(option));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ref, valueAsCode],
  );

  useEffect(() => {
    if (!value || options.length === 0) return;
    const code = isObject(value) ? value.value : value;
    const found = options.find((o) => o.value === code);
    if (found) setText(displayOf(found));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value, valueAsCode]);
  return (
    <Box sx={{ width: '100%' }}>
      {displayName ?? (
        <InputLabel>
          <Typography variant={'body_md'}>{displayName}</Typography>
        </InputLabel>
      )}
      <Stack
        direction={'row'}
        spacing={1}
        alignItems={'center'}
      >
        <Autocomplete
          value={value === '' ? null : value}
          inputValue={text}
          size="small"
          sx={{ width: '100%' }}
          groupBy={(option) => 1}
          filterOptions={(x) => x}
          onInputChange={(e, val, reason) => {
            if (reason === 'input') setText(val);
            else if (reason === 'clear') setText('');
          }}
          renderGroup={(item) => {
            const { key, children } = item;
            return <Box key={key}>{children}</Box>;
          }}
          isOptionEqualToValue={(option, value) =>
            isObject(value)
              ? option.value === value.value || option.label === value.label
              : option.value === value
          }
          PaperComponent={(props) => (
            <Card
              {...props}
              padding={0}
            />
          )}
          renderInput={(params) => {
            const { inputProps } = params;
            ref.current = inputProps;
            return (
              <TextField
                {...params}
                sx={{ ...inputStyles, ...errorInputBgStyles }}
                error={error}
                label={displayName}
                placeholder={placeholder ?? i18n.t('common:message.searching')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: hideSearchIcon ? null : <Icon source={SearchIcon} />,
                }}
                variant={disabled ? 'filled' : 'outlined'}
              />
            );
          }}
          ListboxProps={{
            style: { maxHeight: '300px', padding: 0 },
            position: 'bottom-start',
          }}
          componentsProps={{
            popper: {
              placement: 'bottom-start',
              sx: dropdownFitContent
                ? { width: 'auto !important', minWidth: '320px', maxWidth: '90vw' }
                : undefined,
              modifiers: [
                {
                  name: 'flip',
                  enabled: false,
                },
                {
                  name: 'preventOverflow',
                  enabled: false,
                },
              ],
            },
          }}
          options={options}
          getOptionLabel={(option) => (isObject(option) ? option.label : '')}
          renderOption={(props, option) => {
            const selected = option.value === (isObject(value) ? value?.value : value);
            return (
              <Box
                key={props.id}
                sx={{
                  padding: 'var(--p-space-300) var(--p-space-200)',
                  backgroundColor: selected ? 'rgba(235, 235, 235, 1)' : 'transparent',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(241, 241, 241, 1)',
                  },
                }}
                onClick={() => {
                  handleClick(option);
                }}
              >
                <InlineStack
                  align="space-between"
                  blockAlign="center"
                >
                  <InlineStack>
                    <Flag
                      code={option?.countryCode}
                      width={30}
                    />
                  </InlineStack>
                  <InlineStack
                    gap={300}
                    align="left"
                  >
                    <Typography
                      textAlign={'left'}
                      variant="heading_sm"
                    >
                      {option?.country}
                    </Typography>
                  </InlineStack>

                  <Typography variant="heading_sm">{`${option.value}(${option?.symbol})`}</Typography>
                </InlineStack>
              </Box>
            );
          }}
          disabled={disabled}
          {...others}
        />
      </Stack>
    </Box>
  );
}
